import React from 'react';
import { isMobile, isTablet, isAndroid } from 'react-device-detect';
import { BATTLES_TABS } from 'shared/screens/Battles/constants/dictionary';
import { ASSET_KEY, assetSource } from 'shared/utils/assetSource';
import { getText, TEXT_KEY } from 'shared/utils/localization';
import { numberWithCommas, toOrdinal } from 'shared/utils/number';
import styled from 'styled-components';
import Button from '../../../../components/Button/Button';
import Tab from '../../../../components/Tab/Tab';
import { isNotStandaloneMobilePWA } from '../../../../utils/mobileSupported';
import useBattlesTabContainerStyleCalculator from '../../hooks/useBattlesTabContainerStyleCalculator';
import CollectTabsHeader from './Tabs/CollectTabHeader';
import CollectTabsScreen from './Tabs/CollectTabScreen';
import generateRender from 'shared/utils/generateRender';

const BattlesCollectBase = props => {
  const { rank, rewardValue, standings, handleClaimReward, battlesAssets, isGrandReward, setSelectedTab } = props;

  const { containerStyle, tabBackgroundHeight } = useBattlesTabContainerStyleCalculator();
  return (
    <>
      <PanelBackground>
        <Tab tabContainerTheme={containerStyle} isGrandReward={isGrandReward}>
          <Tab.Navigator tabAction={setSelectedTab} headerComponent={CollectTabsHeader}>
            <Tab.Screen
              tabName={BATTLES_TABS.GAME}
              component={<CollectTabsScreen standings={standings} />}
              battlesAssets={battlesAssets}
              screenContainerTheme={screenContainerTheme({ tabBackgroundHeight, isGrandReward })}
              isGrandReward={isGrandReward}
            />
            <Tab.Screen
              tabName={BATTLES_TABS.STREAK}
              component={<CollectTabsScreen standings={standings} />}
              battlesAssets={battlesAssets}
              screenContainerTheme={screenContainerTheme({ tabBackgroundHeight, isGrandReward })}
              isGrandReward={isGrandReward}
            />
          </Tab.Navigator>
        </Tab>
      </PanelBackground>
      <Panel>
        <InfoWin>
          <StyledText>{`${getText(TEXT_KEY.BATTLES_COLLECT_FINISHED)} ${toOrdinal(rank)}!`}</StyledText>
          <WinText>{getText(TEXT_KEY.BATTLES_COLLECT_WIN)}</WinText>
          <InfoAmount>
            <CoinIcon src={assetSource(ASSET_KEY.COIN_ICON)} alt="Coin Icon" />
            <Amount>{numberWithCommas(rewardValue)}</Amount>
          </InfoAmount>
        </InfoWin>

        <CollectButton
          label={getText(TEXT_KEY.COLLECT_BUTTON)}
          imageSource={assetSource(ASSET_KEY.ROUNDEDRECT_PRIMARY_WIDE)}
          onClick={handleClaimReward}
        />
      </Panel>
    </>
  );
};

export default BattlesCollectBase;

const Panel = styled.section`
  flex: 1;
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  height: 100%;
  padding: 0 0.5rem;
  flex-direction: column;
`;

const PanelBackground = styled(Panel)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: end;
  padding-bottom: 1vw;
  margin-top: 2vw;

  ${({ theme }) => theme.breakpoints.down('xl')} {
    padding-bottom: 1.3vw;
  }

  ${({ theme }) => theme.breakpoints.only('sm')} {
    padding-bottom: 1.15vw;
  }

  ${({ theme }) => theme.platform('android') && `padding-bottom: 1.35vw;`}
  ${({ theme }) => theme.screens.device('mobile-browser') && `justify-content: flex-start;`}
  ${({ theme }) => theme.screens.device('mobile-browser') && theme.platform('ios') && `margin-top: 1vw;`}
  ${({ theme }) =>
    theme.screens.devices(['mobile-standalone', 'tablet-standalone']) && `justify-content: center; margin-top: 1.6vw;`}
    ${({ theme }) =>
    theme.platform('android') &&
    theme.screens.devices(['tablet-browser', 'tablet-standalone']) &&
    `justify-content: center; margin-top: 2.2vw;`}
`;

const isMobileBrowser = isMobile && isNotStandaloneMobilePWA && !isTablet;

function getBottomValue({ isGrandReward, tabBackgroundHeight }) {
  const getBottomValueDefault = () => {
    if (!isMobile) return `${tabBackgroundHeight * 0.095}px`;

    if (isTablet) return isGrandReward ? `${tabBackgroundHeight * 0.06}px` : `${tabBackgroundHeight * 0.085}px`;

    if (isGrandReward) return isAndroid ? `${tabBackgroundHeight * 0.05}px` : `${tabBackgroundHeight * 0.07}px`;
    return isAndroid ? `${tabBackgroundHeight * 0.08}px` : `${tabBackgroundHeight * 0.1}px`;
  };

  const getBottomValueMillelacs = () => {
    if (!isMobile) return `${tabBackgroundHeight * 0.105}px`;

    if (isTablet) return isGrandReward ? `${tabBackgroundHeight * 0.06}px` : `${tabBackgroundHeight * 0.085}px`;

    if (isGrandReward) return isAndroid ? `${tabBackgroundHeight * 0.05}px` : `${tabBackgroundHeight * 0.07}px`;
    return isAndroid ? `${tabBackgroundHeight * 0.08}px` : `${tabBackgroundHeight * 0.1}px`;
  };

  const getBottomValueSga = () => {
    if (!isMobile) return `${tabBackgroundHeight * 0.085}px`;

    if (isTablet) return isGrandReward ? `${tabBackgroundHeight * 0.07}px` : `${tabBackgroundHeight * 0.075}px`;

    if (isGrandReward) return isAndroid ? `${tabBackgroundHeight * 0.06}px` : `${tabBackgroundHeight * 0.07}px`;
    return isAndroid ? `${tabBackgroundHeight * 0.075}px` : `${tabBackgroundHeight * 0.085}px`;
  };

  const getBottomValueJamul = () => {
    if (!isMobile) return `${tabBackgroundHeight * 0.105}px`;

    if (isTablet) return isGrandReward ? `${tabBackgroundHeight * 0.06}px` : `${tabBackgroundHeight * 0.08}px`;

    if (isGrandReward) return isAndroid ? `${tabBackgroundHeight * 0.05}px` : `${tabBackgroundHeight * 0.07}px`;
    return `${tabBackgroundHeight * 0.08}px`;
  };

  return generateRender({
    default: getBottomValueDefault(),
    jamul: getBottomValueJamul(),
    sga: getBottomValueSga(),
    millelacs: getBottomValueMillelacs(),
  });
}

const screenContainerTheme = ({ isGrandReward, tabBackgroundHeight }) => ({
  bottom: getBottomValue({ isGrandReward, tabBackgroundHeight }),
  width: '95%',
  height: isMobileBrowser ? '14vh' : '9vw',
  justifyContent: 'center',
  alignItems: 'flex-end',
  padding: '0',
});

const InfoWin = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  align-items: center;
`;

const StyledText = styled.span`
  color: #ffffff;
  line-height: 1;
  font-size: 2vw;
  text-align: center;
  font-weight: 600;
`;

const WinText = styled.span`
  background: linear-gradient(to bottom, #ffa800 0%, #ffe78b 83%, #fff3c3 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 2.5vw;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
`;

const InfoAmount = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: center;
  align-items: center;
`;

const CoinIcon = styled.img`
  position: relative;
  width: 3vh;
  height: 3vh;
  margin: 0 0.25vh;
  top: 3px;
`;

const Amount = styled(StyledText)`
  text-transform: uppercase;
`;

const CollectButton = styled(Button).attrs({
  containerStyle: `
  position: relative;
  width: 100%;`,
  imageStyle: `width: 100%;`,
  textStyle: `
    font-size: 2vw;
    text-transform: uppercase;
    `,
})``;

